'use client';

import type { TextInputProps } from '../text-input';

import {
  formatBirthDate,
  isBirthDateValid,
  unformatBirthDate,
} from '@polyseme/utils/date/date-utils';
import mergeRefs from 'merge-refs';
import { forwardRef, useEffect, useRef } from 'react';

import { TextInput } from '../text-input';

type DateInputProps = Omit<TextInputProps, 'onChange' | 'value'> & {
  onChange: (value: string) => void;
  value?: string;
};

const MASK_OPTIONS = { mask: '__/__/____', replacement: { _: /\d/ } } as const;

/**
 * Replace the input content programmatically by selecting all then inserting
 * digits via execCommand so the mask library processes them through its
 * normal beforeinput flow.
 */
const replaceInputValue = (input: HTMLInputElement, ddmmyyyy: string) => {
  const digits = ddmmyyyy.replace(/\D/g, '');
  input.focus();
  input.setSelectionRange(0, input.value.length);
  document.execCommand('insertText', false, digits);
};

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  ({ onChange, value, ...restProps }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const lastEmittedValue = useRef(value);

    // Sync input when value changes externally (e.g. RHF reset/setValue)
    useEffect(() => {
      if (value === lastEmittedValue.current) return;
      lastEmittedValue.current = value;
      if (inputRef.current) {
        const display = value ? unformatBirthDate(value) : '';
        if (display) {
          replaceInputValue(inputRef.current, display);
        } else {
          inputRef.current.value = '';
        }
      }
    }, [value]);

    const handleChange = ({
      target: { value: raw },
    }: React.ChangeEvent<HTMLInputElement>) => {
      if (isBirthDateValid(raw)) {
        const formatted = formatBirthDate(raw);
        lastEmittedValue.current = formatted;
        onChange(formatted);
      } else {
        lastEmittedValue.current = raw;
        onChange(raw);
      }
    };

    return (
      <TextInput
        ref={mergeRefs(ref, inputRef)}
        {...restProps}
        defaultValue={value ? unformatBirthDate(value) : undefined}
        mask={MASK_OPTIONS}
        onChange={handleChange}
      />
    );
  }
);

DateInput.displayName = 'DateInput';
